import React from 'react';
import { Link } from 'react-router-dom';
import GameCTA from '../../components/GameCTA';
import SocialProof from '../../components/SocialProof';

const NightWanders: React.FC = () => {
  return (
    <div className="min-h-screen bg-tal-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Link to="/games" className="text-tal-blue-600 hover:text-tal-blue-700 flex items-center gap-2 font-medium transition-colors">
            ← Back to Games
          </Link>
        </div>

        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* main-banner */}
          <div
            className="bg-gradient-to-r from-tal-brown-600/70 to-tal-blue-600/70 px-8 py-16 text-white text-center bg-cover bg-center bg-blend-overlay"
            style={{ backgroundImage: "url('/games/NW_header.jpg')" }}
          >
            <h1 className="text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), -2px -2px 4px rgba(0,0,0,0.8), 2px -2px 4px rgba(0,0,0,0.8), -2px 2px 4px rgba(0,0,0,0.8)' }}>Night Wanders</h1>
            <p className="text-xl opacity-90" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8), -1px -1px 3px rgba(0,0,0,0.8), 1px -1px 3px rgba(0,0,0,0.8), -1px 1px 3px rgba(0,0,0,0.8)' }}>A College Mystery Adventure</p>
            <div className="mt-6">
              <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                In Development
              </span>
            </div>
          </div>

          <div className="px-8 py-10 space-y-8">
            {/* Overview */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Overview
              </h2>
              <p className="text-tal-slate-700 leading-relaxed text-lg mb-6">
                Night Wanders is an immersive narrative adventure that takes you through mysterious college landscapes
                where shadows hide secrets and every choice illuminates a different path. As darkness falls, the world
                transforms, revealing hidden truths that remain concealed in daylight.
              </p>
              <div className="flex items-center justify-center gap-3">                
                <SocialProof
                  links={[
                    { url: 'https://www.facebook.com/profile.php?id=61574532887730#' },
                    { url: 'https://www.instagram.com/play.my.fate' }
                  ]}
                />
              </div>
            </section>

            {/* Story */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Story
              </h2>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                You awaken in a world caught between twilight and dawn, where the boundaries between reality and dreams
                blur with each passing moment. As a Night Wanderer, you possess the unique ability to perceive what
                others cannot see—the whispers of the past, the echoes of forgotten memories, and the threads of fate
                that bind all living things.
              </p>
              <p className="text-tal-slate-700 leading-relaxed">
                Your journey begins when mysterious disappearances plague a small coastal town. As you investigate,
                you'll uncover an ancient conspiracy that spans centuries, where light and shadow wage an eternal battle
                for the soul of the world.
              </p>
            </section>

            {/* Key Features */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Key Features
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                <div className="bg-tal-blue-50 p-6 rounded-lg border-l-4 border-tal-blue-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">📖 Branching Narrative</h3>
                  <p className="text-tal-slate-700">
                    Your choices shape the story. Every decision creates ripples that affect characters, locations,
                    and the ultimate outcome of your journey.
                  </p>
                </div>
                <div className="bg-tal-blue-50 p-6 rounded-lg border-l-4 border-tal-blue-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">🎭 Complex Characters</h3>
                  <p className="text-tal-slate-700">
                    Meet a diverse cast of characters, each with their own secrets, motivations, and stories that
                    intertwine with yours in unexpected ways.
                  </p>
                </div>
                <div className="bg-tal-blue-50 p-6 rounded-lg border-l-4 border-tal-blue-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">🎨 Atmospheric Art Style</h3>
                  <p className="text-tal-slate-700">
                    Immerse yourself in beautifully crafted environments that blend realism with surreal elements,
                    creating a unique visual experience.
                  </p>
                </div>
              </div>
            </section>

            {/* Development Status */}
            <section className="bg-tal-brown-50 rounded-lg p-6 border-l-4 border-tal-brown-500">
              <h2 className="text-2xl font-bold text-tal-slate-900 mb-4">Development Status</h2>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                Night Wanders is currently in active development. We're crafting a deep, meaningful experience that
                honors the player's intelligence and emotional investment.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-tal-slate-700 font-medium">Story & Narrative</span>
                  <span className="text-tal-blue-600 font-semibold">98%</span>
                </div>
                <div className="w-full bg-tal-slate-200 rounded-full h-2">
                  <div className="bg-tal-blue-600 h-2 rounded-full" style={{ width: '98%' }}></div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-tal-slate-700 font-medium">Art & Visuals</span>
                  <span className="text-tal-blue-600 font-semibold">90%</span>
                </div>
                <div className="w-full bg-tal-slate-200 rounded-full h-2">
                  <div className="bg-tal-blue-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-tal-slate-700 font-medium">Programming & Systems</span>
                  <span className="text-tal-blue-600 font-semibold">99%</span>
                </div>
                <div className="w-full bg-tal-slate-200 rounded-full h-2">
                  <div className="bg-tal-blue-600 h-2 rounded-full" style={{ width: '99%' }}></div>
                </div>
              </div>
            </section>

            {/* Stay Updated */}
            <GameCTA
              title="Stay Updated"
              description="Be the first to know when Night Wanders releases. Subscribe to our newsletter for exclusive updates, behind-the-scenes content, and more."
              gradientFrom="from-tal-brown-600"
              gradientTo="to-tal-blue-600"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NightWanders;
